import clsx from 'clsx';
import { FC, useState } from 'react';
import { Icon, IconType } from 'atoms/Icon';
import { Link } from 'atoms/Link/Link';
import { Span } from 'atoms/Typography';
import { useProductDetailInfo } from 'pages/ProductDetail/hooks/useProductDetailInfo';
import { useTranslations } from 'modules/i18n/useTranslations';
import { Tooltip } from '../Tooltip';
import { WarrantyInfo } from '../WarrantyInfo';
import styles from './ProductBullets.module.css';

const prepareColumns = (bullets: string[], tooltips: boolean = false) => {
  const maxBullets = 5;
  const priceIndex = 3;
  const warrantyIndex = 4;
  const slicedBullets = bullets.slice(0, maxBullets);
  const column1Elements = [...slicedBullets.slice(0, 3)];
  const column2Elements = [...slicedBullets.slice(3)];

  if (tooltips) {
    column1Elements.splice(priceIndex, 0, 'price');
    column2Elements.splice(warrantyIndex - 2, 0, 'warranty');
  }

  return { column1Elements, column2Elements };
};

const PriceHelper: FC = () => {
  const t = useTranslations();
  const { priceLink } = useProductDetailInfo();
  return (
    <li className={styles.bulletItem}>
      {t('product.pdp.helpers.price')}{' '}
      <Link href={priceLink?.url || '#'} target="_blank">
        <Icon type={IconType.info} className={styles.infoIcon} />
      </Link>
    </li>
  );
};

const WarrantyHelper: FC = () => {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const handleTooltip = (state: boolean = true) => {
    setOpen(state);
  };
  return (
    <li className={styles.bulletItem}>
      {t('product.pdp.helpers.warranty')}{' '}
      <Tooltip
        onClose={() => {
          handleTooltip(false);
        }}
        open={open}
        element={<WarrantyInfo />}
        className={styles.tooltip}
      >
        <Span
          onClick={() => {
            handleTooltip(true);
          }}
        >
          <Icon type={IconType.info} className={styles.infoIcon} />
        </Span>
      </Tooltip>
    </li>
  );
};

const Bullet: FC<{ bullet: string }> = ({ bullet }) => {
  if (bullet === 'price') {
    return <PriceHelper />;
  }
  if (bullet === 'warranty') {
    return <WarrantyHelper />;
  }
  return <li className={styles.bulletItem}>{bullet}</li>;
};

export const ProductBullets: FC<{
  bullets?: string[];
  tooltips?: boolean;
  className?: string;
}> = ({ bullets, tooltips, className }) => {
  if (!bullets || bullets.length === 0) {
    return null;
  }

  const { column1Elements, column2Elements } = prepareColumns(
    bullets,
    tooltips,
  );

  return (
    <div className={clsx(styles.bulletListContainer, className)}>
      <ul className={clsx(styles.bulletList, tooltips && styles.withTooltips)}>
        {column1Elements.map((bullet, index) => (
          <Bullet key={index} bullet={bullet} />
        ))}
      </ul>
      <ul className={clsx(styles.bulletList, tooltips && styles.withTooltips)}>
        {column2Elements.map((bullet, index) => (
          <Bullet key={index} bullet={bullet} />
        ))}
      </ul>
    </div>
  );
};
